<script setup lang="ts">
import Routes from '@/constants/Routes';
import { t } from '@/i18n';
import { useAccountService } from '@/services/api/accountService';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { DxTabs, type DxTabsTypes } from 'devextreme-vue/tabs';
import Icons from '@/constants/Icons';
import { svgIcon } from '@/helpers/iconHelpers';

definePage({
  name: 'AccountRoot',
  redirect: { name: Routes.Account.Profile },
  meta: {
    title: 'account.title'
  }
});

const route = useRoute();
const router = useRouter();
const selectedItem = ref<number>();
// A user who signs in with Microsoft has no password or MFA here: the tenant handles both.
const signsInWithMicrosoft = ref(false);
// Getters, so the tabs read the current locale.
const allItems = [
  { get text(): string { return t('account.tabs.profile'); }, icon: svgIcon(Icons.AddressCard), routeName: Routes.Account.Profile },
  { get text(): string { return t('account.tabs.email'); }, icon: svgIcon(Icons.At), routeName: Routes.Account.Email },
  { get text(): string { return t('account.tabs.password'); }, icon: svgIcon(Icons.Unlock), routeName: Routes.Account.Password, local: true },
  { get text(): string { return t('mfa.title'); }, icon: svgIcon(Icons.Key), routeName: Routes.Account.Mfa, local: true }
];
const items = computed(() => allItems.filter(x => !(signsInWithMicrosoft.value && x.local)));

async function tabItemClick(e: DxTabsTypes.ItemClickEvent): Promise<void> {
  await router.push({ name: e.itemData.routeName });
}

// The MFA pages (AccountMfaEnable, AccountMfaReset, …) sit beside AccountMfa rather than under it, so the tab is
// found by name: each page's name starts with its tab's.
function updateSelection(): void {
  const name = String(route.name ?? '');
  const item = items.value.find(x => name.startsWith(x.routeName));
  if (item) {
    selectedItem.value = items.value.indexOf(item);
  }
}

onMounted(async () => {
  updateSelection();
  signsInWithMicrosoft.value = !!(await useAccountService().getProfile())?.entraAccount;
  updateSelection();
});

watch(route, updateSelection);
</script>

<template>
  <div class="py-2 px-3">
    <h5 class="m-0">{{ $t('account.title') }}</h5>
    <small class="text-body-secondary">{{ $t('account.subtitle') }}</small>
  </div>
  <div>
    <DxTabs v-model:selected-index="selectedItem" :data-source="items" :scroll-by-content="true"
        :show-nav-buttons="true" @item-click="tabItemClick" />
  </div>
  <div class="py-2 px-3">
    <div class="container">
      <RouterView />
    </div>
  </div>
</template>

<style scoped>

</style>